/**
 * Validation utilities for form inputs using Zod schemas
 */

import { z } from 'zod';
import { WorkOrder, Client, Technician, Priority, WorkOrderStatus } from '../types';

export interface ValidationError {
    field: string;
    message: string;
}

/**
 * Zod Schemas
 */
export const workOrderValidationSchema = z.object({
    title: z.string({ message: 'El título de la orden es obligatorio' })
        .trim()
        .min(1, 'El título de la orden es obligatorio'),
    description: z.string({ message: 'La descripción es obligatoria' })
        .trim()
        .min(1, 'La descripción es obligatoria'),
    clientName: z.string({ message: 'El nombre del cliente es obligatorio' })
        .trim()
        .min(1, 'El nombre del cliente es obligatorio'),
    scheduledDate: z.string({ message: 'La fecha programada es obligatoria' })
        .refine((val) => !isNaN(Date.parse(val)), { message: 'La fecha programada no es válida' }),
    priority: z.nativeEnum(Priority, { message: 'Prioridad inválida' }).optional(),
    status: z.nativeEnum(WorkOrderStatus, { message: 'Estado inválido' }).optional(),
    actualHours: z.number({ message: 'Las horas no pueden ser negativas' })
        .min(0, 'Las horas no pueden ser negativas')
        .optional()
});

export const clientValidationSchema = z.object({
    name: z.string({ message: 'El nombre del cliente es obligatorio' })
        .trim()
        .min(1, 'El nombre del cliente es obligatorio'),
    email: z.string().nullable().optional().or(z.literal(''))
        .refine((val) => !val || isValidEmail(val), { message: 'El email no es válido' }),
    phone: z.string().nullable().optional().or(z.literal(''))
        .refine((val) => !val || isValidPhone(val), { message: 'El teléfono no es válido' })
});

export const technicianValidationSchema = z.object({
    name: z.string({ message: 'El nombre del técnico es obligatorio' })
        .trim()
        .min(1, 'El nombre del técnico es obligatorio'),
    category: z.string({ message: 'La especialidad es obligatoria' })
        .min(1, 'La especialidad es obligatoria'),
    email: z.string().nullable().optional().or(z.literal(''))
        .refine((val) => !val || isValidEmail(val), { message: 'El email no es válido' }),
    phone: z.string().nullable().optional().or(z.literal(''))
        .refine((val) => !val || isValidPhone(val), { message: 'El teléfono no es válido' })
});

/**
 * Validates work order data before creation/update
 */
export const validateWorkOrder = (data: Partial<WorkOrder> | Record<string, any>): ValidationError[] => {
    const result = workOrderValidationSchema.safeParse(data);
    if (result.success) return [];
    return result.error.issues.map((issue) => ({
        field: String(issue.path[0]),
        message: issue.message
    }));
};

/**
 * Validates client data before creation/update
 */
export const validateClient = (data: Partial<Client> | Record<string, any>): ValidationError[] => {
    const result = clientValidationSchema.safeParse(data);
    if (result.success) return [];
    return result.error.issues.map((issue) => ({
        field: String(issue.path[0]),
        message: issue.message
    }));
};

/**
 * Validates technician data before creation/update
 */
export const validateTechnician = (data: Partial<Technician> | Record<string, any>): ValidationError[] => {
    const result = technicianValidationSchema.safeParse(data);
    if (result.success) return [];
    return result.error.issues.map((issue) => ({
        field: String(issue.path[0]),
        message: issue.message
    }));
};

/**
 * Validates email format
 */
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validates phone format (Relaxed for international formats)
 */
export const isValidPhone = (phone: string): boolean => {
    // Remove spaces and common separators
    const cleaned = phone.replace(/[\s\-()]/g, '');

    // Allow optional '+' followed by 7 to 15 digits
    const phoneRegex = /^\+?[0-9]{7,15}$/;
    return phoneRegex.test(cleaned);
};

/**
 * Validates date is not in the past (with optional tolerance)
 */
export const validateFutureDate = (dateString: string, allowPast: boolean = false): ValidationError | null => {
    const futureDateSchema = z.string()
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Fecha inválida' });

    const result = futureDateSchema.safeParse(dateString);
    if (!result.success) {
        return { field: 'date', message: result.error.issues[0].message };
    }

    if (!allowPast) {
        const date = new Date(dateString);
        const now = new Date();
        if (date < now) {
            return { field: 'date', message: 'La fecha no puede estar en el pasado' };
        }
    }

    return null;
};

/**
 * Formats validation errors into a user-friendly message
 */
export const formatValidationErrors = (errors: ValidationError[]): string => {
    if (errors.length === 0) return '';

    if (errors.length === 1) {
        return errors[0].message;
    }

    return 'Se encontraron los siguientes errores:\n' +
        errors.map((e, i) => `${i + 1}. ${e.message}`).join('\n');
};
